"use client";

import { Info, Sparkle } from "lucide-react";
import { Text } from "@/components/ui/text";
import { DEAL_MOMENTUM_MIN_TRANSCRIPTS_MESSAGE } from "@/lib/no-transcript";
import type { DealMomentumBlocker } from "@/api-client/meetings";
import type { CallScoringDealMomentumBlocker } from "@/api-client/call-scoring";

export type MomentumBlockerItem = DealMomentumBlocker | CallScoringDealMomentumBlocker;
import {
  DEAL_MOMENTUM_DISCLAIMER,
  DEAL_MOMENTUM_EMPTY_BLOCKERS_MESSAGE,
  formatMomentumBlockerLabel,
  getMomentumBlockerIcon,
  MOMENTUM_BLOCKER_BADGE_CLASS,
  MOMENTUM_BLOCKERS_PANEL_CLASS,
} from "@/lib/momentum-blocker";
import { cn } from "@/lib/utils";

export interface DealMomentumBlockerRowProps {
  blocker: MomentumBlockerItem;
  className?: string;
}

function DealMomentumMinTranscriptsMessage({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Info className="h-5 w-5 shrink-0 text-muted-foreground opacity-50" />
      <Text size="base" weight="normal" color="muted">
        {DEAL_MOMENTUM_MIN_TRANSCRIPTS_MESSAGE}
      </Text>
    </div>
  );
}

export function DealMomentumBlockerRow({ blocker, className }: DealMomentumBlockerRowProps) {
  const Icon = getMomentumBlockerIcon(blocker.key);
  const label = formatMomentumBlockerLabel(blocker);

  return (
    <div className={cn("flex items-start gap-4", className)}>
      <span className={cn(MOMENTUM_BLOCKER_BADGE_CLASS, "shrink-0")}>
        <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
        {label || "Indicator"}
      </span>
      <Text
        as="p"
        size="sm"
        weight="normal"
        color="muted"
        className="min-w-0 flex-1 leading-relaxed pt-0.5"
      >
        {blocker.reason?.trim() || "—"}
      </Text>
    </div>
  );
}

export interface DealMomentumBlockersListProps {
  blockers: MomentumBlockerItem[];
  hasDealMomentumData?: boolean;
  emptyMessage?: string;
  className?: string;
}

export function DealMomentumBlockersList({
  blockers,
  hasDealMomentumData = true,
  emptyMessage = DEAL_MOMENTUM_EMPTY_BLOCKERS_MESSAGE,
  className,
}: DealMomentumBlockersListProps) {
  if (hasDealMomentumData === false) {
    return <DealMomentumMinTranscriptsMessage className="py-2" />;
  }

  if (!blockers.length) {
    return (
      <Text as="p" size="sm" weight="normal" color="muted">
        {emptyMessage}
      </Text>
    );
  }

  return (
    <div className={cn("flex flex-col gap-5", className)}>
      {blockers.map((blocker) => (
        <DealMomentumBlockerRow key={blocker.key} blocker={blocker} />
      ))}
    </div>
  );
}

export interface DealMomentumBlockersPanelProps {
  blockers: MomentumBlockerItem[];
  hasDealMomentumData?: boolean;
  title?: string;
  /** Product disclaimer under the blocker list (not the API `note` field). */
  disclaimer?: string;
  showDisclaimer?: boolean;
  emptyMessage?: string;
  className?: string;
}

export function DealMomentumBlockersPanel({
  blockers,
  hasDealMomentumData = true,
  title,
  disclaimer = DEAL_MOMENTUM_DISCLAIMER,
  showDisclaimer = true,
  emptyMessage,
  className,
}: DealMomentumBlockersPanelProps) {
  const showDisclaimerFooter = hasDealMomentumData !== false && showDisclaimer;

  return (
    <div className={cn(MOMENTUM_BLOCKERS_PANEL_CLASS, className)}>
      {title ? (
        <Text as="h3" size="base" weight="semibold" color="default">
          {title}
        </Text>
      ) : null}

      <DealMomentumBlockersList
        blockers={blockers}
        hasDealMomentumData={hasDealMomentumData}
        emptyMessage={emptyMessage}
      />

      {showDisclaimerFooter && disclaimer.trim() ? (
        <Text
          as="p"
          size="xs"
          weight="normal"
          color="muted"
          className="!italic leading-relaxed flex items-center gap-1"
        >
        <Sparkle className="h-3 w-3 shrink-0 text-muted-foreground opacity-50" /> {disclaimer.trim()}
        </Text>
      ) : null}
    </div>
  );
}
